import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Upload, UserPlus, Trash2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import ProspectList from "@/components/prospects/ProspectList";
import ProspectForm from "@/components/prospects/ProspectForm";
import ImportProspectsDialog from "@/components/prospects/ImportProspectsDialog";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { deleteAllProspects } from "@/lib/prospectsApi";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const Prospects = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { userRole } = useAuth();
  const { toast } = useToast();

  const canCreate = userRole === "ceo" || userRole === "general_manager" || userRole === "sales_staff";
  const canImport = userRole === "ceo" || userRole === "general_manager" || userRole === "sales_staff";
  const isCeo = userRole === "ceo";

  useEffect(() => {
    if (searchParams.get("import") !== "1") return;
    setIsImportOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("import");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleSuccess = () => {
    setRefreshKey((prev) => prev + 1);
    setIsFormOpen(false);
  };

  const handleDeleteAllConfirm = async () => {
    try {
      setDeletingAll(true);
      const deletedCount = await deleteAllProspects();
      if (deletedCount === 0) {
        toast({
          title: "No prospects to delete",
          description: "The list is already empty.",
        });
        setDeleteAllDialogOpen(false);
        return;
      }

      toast({
        title: "All prospects deleted",
        description: `${deletedCount} prospect${deletedCount === 1 ? "" : "s"} removed.`,
      });
      setDeleteAllDialogOpen(false);
      setRefreshKey((prev) => prev + 1);
    } catch (error: any) {
      toast({
        title: "Error deleting prospects",
        description: error?.message || "Could not delete prospects.",
        variant: "destructive",
      });
    } finally {
      setDeletingAll(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Prospects</h1>
            <p className="text-muted-foreground">
              Track partner outreach and prospecting activity
            </p>
          </div>
          {canCreate && (
            <div className="flex gap-2">
              <Button onClick={() => setIsFormOpen(true)}>
                <UserPlus className="mr-2 h-4 w-4" />
                Add Prospect
              </Button>
              {canImport && (
                <Button variant="outline" onClick={() => setIsImportOpen(true)}>
                  <Upload className="mr-2 h-4 w-4" />
                  Import Prospects
                </Button>
              )}
            </div>
          )}
        </div>

        <ProspectList refreshKey={refreshKey} />

        <ProspectForm open={isFormOpen} onOpenChange={setIsFormOpen} onSuccess={handleSuccess} />
        <ImportProspectsDialog
          open={isImportOpen}
          onOpenChange={setIsImportOpen}
          onImported={() => setRefreshKey((prev) => prev + 1)}
        />

        <AlertDialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete all prospects</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete all prospects from this screen. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingAll}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAllConfirm}
                disabled={deletingAll}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deletingAll ? "Deleting..." : "Delete all"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default Prospects;
